import { useEffect } from "react";

const SITE_URL = "https://geniex.in";
const DEFAULT_OG = "https://geniex.in/renders/dynamic-hero.png";

/**
 * Per-page SEO hook. Updates <title>, description, canonical, Open Graph
 * and Twitter card tags as the user navigates. Falls back to sensible
 * GenieX defaults so every route ships with unique, premium metadata.
 */
export function useSEO({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  path,
  keywords,
  jsonLd,
  noindex,
} = {}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} | GenieX`
      : "GenieX | Intelligent Living & Smart Home Automation";

    document.title = fullTitle;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const resolvedCanonical =
      canonical ||
      (path
        ? `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`
        : `${SITE_URL}${window.location.pathname}`);
    const resolvedOgTitle = ogTitle || fullTitle;
    const resolvedOgDescription = ogDescription || description;
    const resolvedOgImage = ogImage || DEFAULT_OG;

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    setMeta("og:title", resolvedOgTitle, "property");
    setMeta("og:description", resolvedOgDescription, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", "GenieX", "property");
    setMeta("og:url", resolvedCanonical, "property");
    setMeta("og:image", resolvedOgImage, "property");
    setMeta("og:locale", "en_IN", "property");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", resolvedOgTitle);
    setMeta("twitter:description", resolvedOgDescription);
    setMeta("twitter:image", resolvedOgImage);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", resolvedCanonical);

    // Structured data (JSON-LD). Accepts a single object or an array of
    // schemas. We tag every node we write so we can clean them up before the
    // next page injects its own — preventing stale Article/FAQ schema from
    // following the user across routes.
    const PREV = document.head.querySelectorAll('script[data-gx-jsonld="1"]');
    PREV.forEach((n) => n.remove());
    if (jsonLd) {
      const nodes = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      nodes.forEach((schema) => {
        if (!schema) return;
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-gx-jsonld", "1");
        s.text = JSON.stringify(schema);
        document.head.appendChild(s);
      });
    }

    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, path, keywords, jsonLd, noindex]);
}
